import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Filter, X } from 'lucide-react';

const TAG_OPTIONS = [
  'Hiking', 'Camping', 'RVing', 'Backpacking', 'Family-Friendly',
  'Pet-Friendly', 'Wheelchair-Accessible', 'Fishing', 'Swimming', 'Wildlife',
];

const SPECIALTY_OPTIONS = [
  'Trail Guide', 'Rock Climbing', 'Kayaking', 'Mountain Biking',
  'Wilderness Survival', 'Photography', 'Bird Watching', 'Fly Fishing',
  'Skiing', 'Navigation',
];

const DiscoveryFiltersSheet = ({
  selectedTags, setSelectedTags,
  selectedSpecialties, setSelectedSpecialties,
  minExperience, setMinExperience,
  maxExperience, setMaxExperience,
}) => {
  const activeCount = selectedTags.length + selectedSpecialties.length + (minExperience > 0 ? 1 : 0);

  const toggleArr = (setter) => (val) => {
    setter((prev) => prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11 sm:h-12 sm:w-12 shrink-0 rounded-full relative"
          data-testid="discovery-filters-btn"
          aria-label="Open filters"
        >
          <Filter className="h-4 w-4" />
          {activeCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="h-[85vh] w-full overflow-y-auto sm:h-auto sm:max-h-[90vh] sm:w-[540px] sm:!side-right"
        data-testid="discovery-filters-sheet"
      >
        <SheetHeader>
          <SheetTitle>Filters</SheetTitle>
          <SheetDescription>
            Refine your search by tags, specialties, and experience
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Tags Filter */}
          <div>
            <h3 className="font-semibold mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {TAG_OPTIONS.map((tag) => (
                <Badge
                  key={tag}
                  variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleArr(setSelectedTags)(tag)}
                >
                  {tag}
                  {selectedTags.includes(tag) && <X className="h-3 w-3 ml-1" />}
                </Badge>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedTags([])} className="mt-2">
                Clear Tags
              </Button>
            )}
          </div>

          {/* Specialties Filter */}
          <div>
            <h3 className="font-semibold mb-3">Specialties (Guides)</h3>
            <div className="flex flex-wrap gap-2">
              {SPECIALTY_OPTIONS.map((specialty) => (
                <Badge
                  key={specialty}
                  variant={selectedSpecialties.includes(specialty) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleArr(setSelectedSpecialties)(specialty)}
                >
                  {specialty}
                  {selectedSpecialties.includes(specialty) && <X className="h-3 w-3 ml-1" />}
                </Badge>
              ))}
            </div>
            {selectedSpecialties.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedSpecialties([])} className="mt-2">
                Clear Specialties
              </Button>
            )}
          </div>

          {/* Years of Experience Filter */}
          <div>
            <h3 className="font-semibold mb-3">Years of Experience (Guides)</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Minimum</span>
                  <span className="text-sm font-medium">{minExperience} years</span>
                </div>
                <Slider
                  value={[minExperience]}
                  onValueChange={(value) => setMinExperience(value[0])}
                  max={50}
                  step={1}
                  className="w-full"
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Maximum</span>
                  <span className="text-sm font-medium">{maxExperience}+ years</span>
                </div>
                <Slider
                  value={[maxExperience]}
                  onValueChange={(value) => setMaxExperience(value[0])}
                  max={50}
                  step={1}
                  className="w-full"
                />
              </div>
              {(minExperience > 0 || maxExperience < 50) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setMinExperience(0); setMaxExperience(50); }}
                >
                  Reset Experience Filter
                </Button>
              )}
            </div>
          </div>

          {/* Clear All Filters */}
          {(selectedTags.length > 0 || selectedSpecialties.length > 0 || minExperience > 0 || maxExperience < 50) && (
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => {
                setSelectedTags([]);
                setSelectedSpecialties([]);
                setMinExperience(0);
                setMaxExperience(50);
              }}
            >
              Clear All Filters
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DiscoveryFiltersSheet;
